import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import WingmanLogo from './WingmanLogo';
import { colors } from './SharedComponents';
import { LAYOUT_TOP_OFFSET } from '../config';
import { useAuth } from '../AuthContext';

// THE RAIL (Lane 1363, Ryan 2026-10-02): "By default, get rid of this entire
// left navigation panel. The only time it ever expands out is if you hover
// over one of the buttons to see what it is."
//
// The nav rests as an icon rail of RAIL_WIDTH. Pointer hover or keyboard
// focus inside it widens it to NAV_EXPANDED_WIDTH with the labels showing.
// Ryan, after testing the first overlay version: the page must MAKE ROOM, not
// be covered. So the rail reports its state (`onExpandedChange`) and
// AppLayout publishes the current width as the CSS variable NAV_WIDTH_VAR;
// the header, the page and any fixed element that is anchored to the left
// read it and slide over with the same transition (NAV_TRANSITION).
//
// Rapid hover must not shake the whole page: a pointer that only crosses the
// rail opens nothing (HOVER_OPEN_DELAY_MS), and leaving closes it only after
// COLLAPSE_DELAY_MS, so coming straight back keeps it open. Keyboard focus
// opens at once. Choosing an item closes it. There is no pin and no stored
// preference: the old "<"/">" toggle is gone, and nothing is read from storage.
//
// Touch screens cannot hover, so (only there) a small edge button opens the
// rail; a tap outside or a navigation closes it.
export const RAIL_WIDTH = 72;
export const NAV_EXPANDED_WIDTH = 240;
export const NAV_WIDTH_VAR = '--app-nav-width';
export const NAV_TRANSITION_MS = 200;
const NAV_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
// `transition` value for one CSS property that follows the rail.
export const navTransition = (property) => `${property} ${NAV_TRANSITION_MS}ms ${NAV_EASE}`;
const HOVER_OPEN_DELAY_MS = 80;
const COLLAPSE_DELAY_MS = 150;
// A tap fires emulated mouse events; ignore hover for this long after one.
const TOUCH_GRACE_MS = 800;

function deviceCanHover() {
  try {
    if (typeof window.matchMedia !== 'function') return true;
    return window.matchMedia('(hover: hover)').matches;
  } catch (e) {
    return true;
  }
}

// Labels stay in the DOM while collapsed (they are each link's accessible
// name), hidden visually only.
const visuallyHidden = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
  overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
};

// `colors` now comes from the shared design-system source (imported above, R2
// Lane 77). The former local copy was a byte-identical fork; its 5 used keys
// (cyan, deepBlue, electricBlue, textMuted, white) match shared exactly.


// Meeting Prep (Lane 805) — rendered only when the env-level
// WINGMAN_TEMPLATE_DECKS_ENABLED flag reaches the frontend via /me
// (AuthContext.templateDecksEnabled; Lane 1410 W4a moved the door from the
// retired creation engines' DECK_MEETING_INTENT_ENABLED to the flag of the page
// that is actually behind it, the template deck library). It exists because it
// IS the Home button relocated: once a user is in a live chat, Home's
// empty-state "Prepare for a meeting" button disappears, so mid-conversation
// there is no route back to the deck library. The nav gives that door a
// permanent home.
//
// POSITION: after Contracts, per Ryan's 2026-09-24 order (see `navItems`).
//
// THE GATE IS NOT OPTIONAL, AND IT IS DELIBERATELY THE SAME ONE Home uses for
// "Prepare for a meeting": with the flag OFF, /decks/meeting-prep has no
// library and no builds, so an UNGATED entry would be a permanent, every-user
// link to an empty room. Carrying the SAME flag means the door tracks the room
// in every environment, whatever the flags happen to be. App.js's
// /decks/meeting-prep route stays registered exactly as it is; this is a door,
// not a room, and Home's gate is likewise button-level only (a single lock).
//
// FRESH FLOW, NOT CARRIED CONTEXT. A plain NavLink navigates with no router
// state, so the page opens clean, byte-identical to what Home's
// `navigate('/decks/meeting-prep')` does.
const meetingPrepNavItem = {
  path: '/decks/meeting-prep', icon: null, label: 'Meeting Prep',
  description: 'Build a meeting deck', iconSvg: 'meetingPrep',
};

// Opportunities (Lane 42) — rendered only when the env-level OPPORTUNITIES_ENABLED
// flag reaches the frontend via /me (AuthContext.opportunitiesEnabled). Sits
// after Analytics. When the flag is OFF this item does not exist.
const opportunitiesNavItem = {
  path: '/opportunities', icon: null, label: 'Opportunities',
  description: 'Savings opportunities', iconSvg: 'opportunities',
};

// Inventory & Ordering (Lane 500) — rendered only when the env-level
// INVENTORY_ORDERING_ENABLED flag reaches the frontend via /me
// (AuthContext.inventoryOrderingEnabled). When the flag is OFF this item does
// not exist and the nav list is byte-identical to today.
//
// ⚠️ IT SITS AFTER SUPPLIERS, NOT AFTER OPPORTUNITIES (Ryan, 2026-07-30, QA
// round 10 decision e). He named the order out loud — *"materials, suppliers,
// inventory, accounts, contracts, RFP builder"* — and confirmed that ONLY this
// item moves. Home, Analytics and Opportunities stay exactly where they are.
// The reasoning it replaces (*"the two 'what should I do' surfaces sit
// together"*) was a lane's, not his.
const inventoryNavItem = {
  path: '/inventory', icon: null, label: 'Inventory',
  description: 'What to order next', iconSvg: 'inventory',
};

// Reports (Lane 1132, Plan A chat workbooks, D6). Rendered when the
// EFFECTIVE creation gate is on (AuthContext.workbookExportEnabled, echoed
// by /me) OR the company already has built reports
// (AuthContext.hasReportArtifacts) — the read-only rollback rule: flag OFF
// with existing artifacts must never strand customer files. Position is
// the plan's ruling (Home -> Reports -> Analytics) until Ryan moved it under
// Meeting Prep on 2026-09-24 (see `navItems`).
const reportsNavItem = {
  path: '/reports', icon: null, label: 'Reports',
  description: 'Built workbooks', iconSvg: 'reports',
};

// Sourcing events (Lane 1322). Rendered only when /me echoes the effective
// gate (AuthContext.sourcingEventsEnabled). It replaces the old RFP Builder,
// whose nav item was removed in Lane 1369.
const sourcingNavItem = {
  path: '/sourcing', icon: null, label: 'Sourcing events',
  description: 'Bid out parts and award', iconSvg: 'sourcing',
};

// Quoting (Lane 1335, RFQ plan D3): right after Contracts. Rendered only when
// /me says the company's Quoting is live (AuthContext.quotingEnabled).
const quotingNavItem = {
  path: '/quoting', icon: null, label: 'Quoting',
  description: 'RFQs sent to Wingman', iconSvg: 'quoting',
};

// Simple SVG nav icons — lightweight, cross-platform consistent
const NavIcon = ({ name, isActive, size = 18 }) => {
  const color = isActive ? colors.white : colors.textMuted;
  const icons = {
    home: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
    analytics: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
    reports: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/></svg>,
    materials: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
    suppliers: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    accounts: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
    contracts: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
    rfp: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="9" y1="3" x2="9" y2="21"/></svg>,
    opportunities: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
    inventory: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7l9-4 9 4v10l-9 4-9-4z"/><path d="M3 7l9 4 9-4"/><path d="M12 11v10"/></svg>,
    intelligence: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a5 5 0 0 0-5 5c0 1.8.9 3 1.8 4 .7.8 1.2 1.5 1.2 2.5h4c0-1 .5-1.7 1.2-2.5.9-1 1.8-2.2 1.8-4a5 5 0 0 0-5-5z"/><line x1="10" y1="18" x2="14" y2="18"/><line x1="10" y1="21" x2="14" y2="21"/></svg>,
    quoting: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z"/><path d="M4 9h16"/><path d="M9 9v11"/></svg>,
    sourcing: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M3 12h12"/><path d="M3 18h8"/><polyline points="16 15 19 18 23 13"/></svg>,
    meetingPrep: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="2" y1="3" x2="22" y2="3"/><path d="M21 3v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3"/><line x1="12" y1="16" x2="15" y2="21"/><line x1="12" y1="16" x2="9" y2="21"/></svg>,
  };
  return <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: size, height: size }}>{icons[name] || null}</span>;
};

// Flag-gated on `intelligencePortalEnabled` (INTELLIGENCE_PORTAL_ENABLED).
// Sits after Meeting Prep and Reports (see `navItems`).
const intelligenceNavItem = {
  path: '/intelligence', icon: null, label: 'Intelligence',
  description: 'What you have told Wingman', iconSvg: 'intelligence',
};

// ONE ordered list, top to bottom, exactly as the nav renders (Ryan,
// 2026-09-24): Home · Analytics · Opportunities · Materials · Suppliers ·
// Customers · Contracts · Meeting Prep · Reports · Intelligence · RFP
// Builder. Inventory was not in that list and keeps its prior slot,
// directly after Suppliers. Later lanes added Quoting and Sourcing events,
// and Lane 1369 removed RFP Builder (the old builder is retired).
//
// An item with a `gate` renders only when that /me flag is true; the others
// always render. Order is the array order and nothing else, so a gated item
// can never be misplaced by another gated item being off. This replaces the
// earlier insert-after-anchor assembly, which could not express this order.
const navItems = [
  { path: '/', icon: null, label: 'Home', description: 'AI Assistant', iconSvg: 'home' },
  { path: '/analytics', icon: null, label: 'Analytics', description: 'Spend & risk metrics', iconSvg: 'analytics' },
  { ...opportunitiesNavItem, gate: 'opportunitiesEnabled' },
  { path: '/materials', icon: null, label: 'Materials', description: 'Material catalog', iconSvg: 'materials' },
  { path: '/suppliers', icon: null, label: 'Suppliers', description: 'Supplier relationships', iconSvg: 'suppliers' },
  { ...inventoryNavItem, gate: 'inventoryOrderingEnabled' },
  { path: '/customers', icon: null, label: 'Customers', description: 'Customers and team notes', iconSvg: 'accounts' },
  { path: '/contracts', icon: null, label: 'Contracts', description: 'Manage contracts', iconSvg: 'contracts' },
  { ...quotingNavItem, gate: 'quotingEnabled' },
  { ...meetingPrepNavItem, gate: 'templateDecksEnabled' },
  { ...reportsNavItem, gate: 'reports' },
  { ...intelligenceNavItem, gate: 'intelligencePortalEnabled' },
  { ...sourcingNavItem, gate: 'sourcingEventsEnabled' },
  // The old "RFP Builder" item (path '/rfp') was removed in Lane 1369 (Ryan,
  // 2026-10-03: "Get rid of the old RFP builder"). Sourcing events above
  // replace it; App.js sends any old /rfp link to /sourcing or Home.
];

function Sidebar({ onExpandedChange }) {
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [canHover, setCanHover] = useState(deviceCanHover);
  const railRef = useRef(null);
  const collapseTimer = useRef(null);
  const openTimer = useRef(null);
  const pointerDown = useRef(false);
  const lastTouch = useRef(0);
  const { opportunitiesEnabled, inventoryOrderingEnabled, templateDecksEnabled,
          intelligencePortalEnabled, workbookExportEnabled,
          hasReportArtifacts, sourcingEventsEnabled, quotingEnabled } = useAuth();

  // One pending change at a time: any new intent cancels both timers.
  const cancelCollapse = useCallback(() => {
    if (collapseTimer.current) {
      clearTimeout(collapseTimer.current);
      collapseTimer.current = null;
    }
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  }, []);
  const open = useCallback(() => {
    cancelCollapse();
    setExpanded(true);
  }, [cancelCollapse]);
  const openSoon = useCallback(() => {
    if (collapseTimer.current) {
      // Back inside the close delay: stay open, nothing moves.
      cancelCollapse();
      return;
    }
    cancelCollapse();
    openTimer.current = setTimeout(() => {
      openTimer.current = null;
      setExpanded(true);
    }, HOVER_OPEN_DELAY_MS);
  }, [cancelCollapse]);
  const collapseNow = useCallback(() => {
    cancelCollapse();
    setExpanded(false);
  }, [cancelCollapse]);
  const collapseSoon = useCallback(() => {
    cancelCollapse();
    collapseTimer.current = setTimeout(() => {
      collapseTimer.current = null;
      setExpanded(false);
    }, COLLAPSE_DELAY_MS);
  }, [cancelCollapse]);

  useEffect(() => cancelCollapse, [cancelCollapse]);

  // Tell the layout, so the page makes room (and gets it back).
  const reportRef = useRef(onExpandedChange);
  reportRef.current = onExpandedChange;
  useEffect(() => {
    if (reportRef.current) reportRef.current(expanded);
  }, [expanded]);

  // A device can gain or lose a hovering pointer (tablet with a mouse).
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    let query;
    try { query = window.matchMedia('(hover: hover)'); } catch (e) { return undefined; }
    if (!query || typeof query.addEventListener !== 'function') return undefined;
    const onChange = () => setCanHover(deviceCanHover());
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // Any navigation, from the rail or elsewhere, leaves the rail collapsed.
  useEffect(() => { collapseNow(); }, [location.pathname, collapseNow]);

  // Touch only: a tap outside the open rail closes it.
  useEffect(() => {
    if (!expanded || canHover) return undefined;
    const onOutside = (e) => {
      if (railRef.current && !railRef.current.contains(e.target)) collapseNow();
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('touchstart', onOutside);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('touchstart', onOutside);
    };
  }, [expanded, canHover, collapseNow]);

  const handleMouseEnter = () => {
    if (!canHover || Date.now() - lastTouch.current < TOUCH_GRACE_MS) return;
    openSoon();
  };
  const handleMouseLeave = () => {
    if (!canHover) return;
    if (openTimer.current) {
      // Only passed across the rail: it never opened, so nothing to close.
      cancelCollapse();
      return;
    }
    collapseSoon();
  };
  // Keyboard focus expands; focus that a click or tap brings does not.
  const handleFocus = () => {
    if (pointerDown.current) return;
    open();
  };
  const handleBlur = (e) => {
    if (railRef.current && railRef.current.contains(e.relatedTarget)) return;
    collapseSoon();
  };
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') collapseNow();
  };
  const markPointerDown = () => { pointerDown.current = true; };
  const markTouch = () => {
    lastTouch.current = Date.now();
    pointerDown.current = true;
  };
  const clearPointerDown = () => { pointerDown.current = false; };

  // Each gated item shows only when its own /me flag is true; with every
  // gate off the list is just the static entries, in the same order.
  const gates = {
    reports: Boolean(workbookExportEnabled || hasReportArtifacts),
    opportunitiesEnabled: Boolean(opportunitiesEnabled),
    inventoryOrderingEnabled: Boolean(inventoryOrderingEnabled),
    templateDecksEnabled: Boolean(templateDecksEnabled),
    intelligencePortalEnabled: Boolean(intelligencePortalEnabled),
    sourcingEventsEnabled: Boolean(sourcingEventsEnabled),
    quotingEnabled: Boolean(quotingEnabled),
  };
  const visibleNavItems = navItems.filter((item) => !item.gate || gates[item.gate]);
  const width = expanded ? NAV_EXPANDED_WIDTH : RAIL_WIDTH;

  return (
    <div
      ref={railRef}
      className="sidebar-container"
      data-expanded={expanded ? 'true' : 'false'}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onMouseDown={markPointerDown}
      onTouchStart={markTouch}
      onMouseUp={clearPointerDown}
      onClickCapture={clearPointerDown}
      style={{
        width: `${width}px`,
        minWidth: `${width}px`,
        height: `calc(100vh - ${LAYOUT_TOP_OFFSET}px)`,
        backgroundColor: colors.deepBlue,
        display: 'flex',
        flexDirection: 'column',
        transition: `${navTransition('width')}, ${navTransition('min-width')}`,
        position: 'fixed',
        left: 0,
        top: LAYOUT_TOP_OFFSET,
        zIndex: 100,
        boxShadow: '4px 0 24px rgba(5, 28, 44, 0.15)',
      }}
    >
      {/* Logo Area. Fixed padding keeps the mark still while the rail widens. */}
      <div
        style={{
          padding: '18px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minHeight: '72px',
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: '36px',
            height: '36px',
            flex: '0 0 36px',
            background: `linear-gradient(135deg, ${colors.electricBlue}, ${colors.cyan})`,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 'var(--font-size-lg)',
            boxShadow: '0 4px 12px rgba(34, 81, 255, 0.3)',
          }}
        >
          <WingmanLogo size={18} fill="white" />
        </div>
        <div
          style={expanded ? {
            color: colors.white,
            fontWeight: 'var(--font-weight-bold)',
            fontSize: 'var(--font-size-base)',
            letterSpacing: '-0.3px',
            whiteSpace: 'nowrap',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          } : visuallyHidden}
        >
          Wingman
        </div>
      </div>

      {/* Navigation Items */}
      <nav aria-label="Main" style={{ flex: 1, padding: '16px 12px', overflowY: 'auto', overflowX: 'hidden' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {visibleNavItems.map((item) => {
            const isActive = location.pathname === item.path ||
              (item.path !== '/' && location.pathname.startsWith(item.path));
            const isHovered = hoveredItem === item.path;

            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={collapseNow}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 15px',
                  borderRadius: '10px',
                  textDecoration: 'none',
                  transition: 'background-color 150ms ease, color 150ms ease',
                  backgroundColor: isActive
                    ? 'rgba(34, 81, 255, 0.15)'
                    : isHovered
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'transparent',
                  position: 'relative',
                  justifyContent: 'flex-start',
                }}
                onMouseEnter={() => setHoveredItem(item.path)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                {/* Active indicator */}
                {isActive && (
                  <div
                    style={{
                      position: 'absolute',
                      left: '0',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '3px',
                      height: '24px',
                      backgroundColor: colors.electricBlue,
                      borderRadius: '0 3px 3px 0',
                    }}
                  />
                )}

                {/* Icon */}
                <NavIcon name={item.iconSvg} isActive={isActive} />

                {/* Label: shown on the open rail, screen-reader only on the closed one */}
                <div style={expanded ? { flex: 1, minWidth: 0 } : visuallyHidden}>
                  <div
                    style={{
                      color: isActive ? colors.white : colors.textMuted,
                      fontSize: 'var(--font-size-base)',
                      fontWeight: isActive ? 'var(--font-weight-semibold)' : 'var(--font-weight-medium)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                    }}
                  >
                    {item.label}
                  </div>
                </div>
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Touch screens only: they cannot hover, so this edge button opens the
          rail. It never pins it open: a tap outside or a navigation closes it. */}
      {!canHover && (
        <button
          type="button"
          onClick={() => (expanded ? collapseNow() : open())}
          aria-expanded={expanded}
          aria-label={expanded ? 'Hide menu names' : 'Show menu names'}
          style={{
            position: 'absolute',
            top: '50%',
            right: '-12px',
            transform: 'translateY(-50%)',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E7EB',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            color: '#6B7280',
            zIndex: 10,
            padding: 0,
          }}
        >
          {expanded ? '‹' : '›'}
        </button>
      )}
    </div>
  );
}

export default Sidebar;
